import { Grid as ChakraGrid } from '@chakra-ui/react';
import {
  Button,
  Grid,
  MegaMenuOverlay,
  MegaMenuReveal,
  MotionBox,
  TextLink,
} from '@time-line/components/common';
import type { Nav, NavDropdownItem } from '@time-line/components/types';
import NextLink from 'next/link';
import type { FocusEvent, KeyboardEvent, PointerEvent } from 'react';
import { useId, useRef } from 'react';

import { RESPONSIVE_BP } from '../constants';
import { HeaderNavItem } from '../HeaderNavItem';
import { SecondaryContent } from './SecondaryContent';

interface MegaMenuProps {
  megaMenuOpenId: string | null;
  setMegaMenuOpenId: (id: string | null) => void;
  primaryNav: Nav['primary'];
  cta?: Nav['cta'];
}

interface MegaMenuDropdownProps {
  item: NavDropdownItem;
  isOpen: boolean;
  isDimmed: boolean;
  setMegaMenuOpenId: (id: string | null) => void;
}

function MegaMenuDropdown({
  item,
  isOpen,
  isDimmed,
  setMegaMenuOpenId,
}: MegaMenuDropdownProps) {
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = () => setMegaMenuOpenId(null);

  return (
    <MotionBox
      className="header_subnav-trigger"
      display="flex"
      alignItems="center"
      // Hover is a mouse-only affordance: on touch the synthetic hover would
      // open the panel and the tap that follows would immediately close it.
      onPointerEnter={(event: PointerEvent) => {
        if (event.pointerType !== 'mouse') return;
        setMegaMenuOpenId(item.label);
      }}
      onPointerLeave={(event: PointerEvent) => {
        if (event.pointerType !== 'mouse') return;
        close();
      }}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !isOpen) return;
        close();
        triggerRef.current?.focus();
      }}
      onBlur={(event: FocusEvent<HTMLDivElement>) => {
        // A null relatedTarget means focus was dropped rather than moved on,
        // which happens when clicking dead space inside the open panel.
        if (
          !event.relatedTarget ||
          event.currentTarget.contains(event.relatedTarget)
        ) {
          return;
        }
        close();
      }}
      lineHeight={1}
    >
      <TextLink
        ref={triggerRef}
        as="button"
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setMegaMenuOpenId(isOpen ? null : item.label)}
        textProps={{ size: 'overline' }}
        variant="underlineOnHover"
        transition="color 0.2s ease-in-out"
        color={isDimmed ? 'blackAlpha.600' : 'gray.600'}
      >
        {item.label}
      </TextLink>
      <MegaMenuReveal
        id={panelId}
        isOpen={isOpen}
        subNav={item.subNav}
        secondaryContent={<SecondaryContent subNav={item.subNav} />}
      />
    </MotionBox>
  );
}

export function MegaMenu({
  megaMenuOpenId,
  setMegaMenuOpenId,
  primaryNav,
  cta,
}: MegaMenuProps) {
  return (
    <Grid.Inner
      position="relative"
      zIndex={0}
      gridColumn="main"
      alignItems="center"
      templateColumns="1fr"
      justifyItems="center"
      display={{ base: 'none', [RESPONSIVE_BP]: 'grid' }}
    >
      <ChakraGrid
        as="nav"
        aria-label="Main menu"
        gridAutoFlow="column"
        gap="sm"
        alignItems="stretch"
      >
        {primaryNav.map((pr, i) => {
          switch (pr.type) {
            case 'dropdown':
              return (
                <MegaMenuDropdown
                  key={i}
                  item={pr}
                  isOpen={megaMenuOpenId === pr.label}
                  isDimmed={!!megaMenuOpenId && megaMenuOpenId !== pr.label}
                  setMegaMenuOpenId={setMegaMenuOpenId}
                />
              );
            case 'link':
              return (
                <HeaderNavItem
                  key={i}
                  href={pr.href}
                  name={pr.name}
                  display="flex"
                  alignItems="center"
                  color={megaMenuOpenId ? 'blackAlpha.600' : 'gray.600'}
                  showIcon={pr.showIcon}
                  isExternal={pr.isExternal}
                >
                  {pr.label}
                </HeaderNavItem>
              );
            default:
              return null;
          }
        })}
        {cta?.href && (
          <Button as={NextLink} href={cta.href} size="sm" variant="outline">
            {cta.label}
          </Button>
        )}
        <MegaMenuOverlay isOpen={!!megaMenuOpenId} />
      </ChakraGrid>
    </Grid.Inner>
  );
}
